import { Link } from "react-router-dom";
import { Star, ShoppingCart, Heart, Eye, GitCompare, BadgePercent, Sparkles, CheckCircle2 } from "lucide-react";
import { productImageUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useFavorites, useCompare } from "../context/FavoritesCompareContext";
import { useI18n } from "../context/I18nContext";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "sonner";

export default function ProductCard({ product, onQuickView }) {
  const { addItem } = useCart();
  const fav = useFavorites();
  const cmp = useCompare();
  const { t } = useI18n();
  const { format: formatGdes } = useCurrency();
  // Show compare_at_price if it's greater than current price; otherwise compute from discount_percent
  const discounted = product.is_promo && product.discount_percent > 0
    ? Math.round(product.price * (100 - product.discount_percent)) / 100
    : null;
  const oldPrice = product.compare_at_price && product.compare_at_price > product.price
    ? product.compare_at_price
    : (discounted ? product.price : null);
  const currentPrice = discounted != null ? discounted : product.price;
  const hasDiscount = oldPrice != null;
  const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(); };

  return (
    <Link to={`/products/${product.product_id}`} className="je-card group relative flex flex-col" data-testid={`product-card-${product.product_id}`}>
      {/* Image cover fills container */}
      <div className="relative w-full overflow-hidden" style={{ height: "220px" }}>
        <img
          src={productImageUrl(product.images?.[0])}
          alt={product.name}
          loading="lazy"
          className="block w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1.5 z-10">
          {product.is_new && (
            <span className="bg-[#0052CC] text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> {t("new")}
            </span>
          )}
          {hasDiscount && (
            <span className="bg-[#E3000F] text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider inline-flex items-center gap-1">
              <BadgePercent className="w-3 h-3" /> −{product.discount_percent > 0 ? product.discount_percent : Math.round(((oldPrice - currentPrice) / oldPrice) * 100)}%
            </span>
          )}
        </div>

        {/* Hover actions right */}
        <div className="absolute top-2 right-2 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <button
            onClick={stop(() => { fav.toggle(product.product_id); toast.success(fav.has(product.product_id) ? "Retiré des favoris" : "Ajouté aux favoris"); })}
            title="Favoris"
            className={`w-8 h-8 rounded-full flex items-center justify-center shadow-md ${fav.has(product.product_id) ? "bg-[#E3000F] text-white" : "bg-white text-gray-700 hover:text-[#E3000F]"}`}
            data-testid={`fav-${product.product_id}`}
          >
            <Heart className={`w-4 h-4 ${fav.has(product.product_id) ? "fill-current" : ""}`} />
          </button>
          {onQuickView && (
            <button
              onClick={stop(() => onQuickView(product))}
              title="Aperçu rapide"
              className="w-8 h-8 rounded-full bg-white text-gray-700 hover:text-[#0052CC] flex items-center justify-center shadow-md"
              data-testid={`quickview-${product.product_id}`}
            >
              <Eye className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={stop(() => { cmp.toggle(product.product_id); toast.success(cmp.has(product.product_id) ? "Retiré du comparateur" : "Ajouté au comparateur"); })}
            title="Comparer"
            className={`w-8 h-8 rounded-full flex items-center justify-center shadow-md ${cmp.has(product.product_id) ? "bg-[#0052CC] text-white" : "bg-white text-gray-700 hover:text-[#0052CC]"}`}
            data-testid={`cmp-${product.product_id}`}
          >
            <GitCompare className="w-4 h-4" />
          </button>
        </div>

        {product.stock <= 0 && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
            <span className="bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded">{t("outOfStock")}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col flex-1">
        {product.brand && <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{product.brand}</span>}
        <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mt-1 group-hover:text-[#0052CC] transition" style={{minHeight: "2.5em"}}>
          {product.name}
        </h3>
        <div className="flex items-center gap-1 mt-1 text-xs">
          {Array.from({length: 5}).map((_, i) => (
            <Star key={i} className={`w-3 h-3 ${i < Math.round(product.rating) ? "je-star fill-current" : "text-gray-300"}`} />
          ))}
          <span className="text-gray-500 ml-0.5">({product.reviews_count || 0})</span>
        </div>
        <div className="mt-2 flex items-baseline gap-2 flex-wrap">
          {hasDiscount ? (
            <>
              <span className="text-base font-bold text-[#E3000F]" style={{fontFamily: "Outfit"}}>{formatGdes(currentPrice)}</span>
              <span className="text-xs text-gray-400 line-through">{formatGdes(oldPrice)}</span>
            </>
          ) : (
            <span className="text-base font-bold text-gray-900" style={{fontFamily: "Outfit"}}>{formatGdes(product.price)}</span>
          )}
        </div>
        <div className="mt-1 flex items-center justify-between">
          {product.stock > 0 ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded"><CheckCircle2 className="w-3 h-3" /> {t("inStock")}</span>
          ) : (
            <span className="text-[10px] font-semibold text-red-700 bg-red-50 px-1.5 py-0.5 rounded">{t("outOfStock")}</span>
          )}
        </div>
        <button
          onClick={stop(() => {
            if (product.stock <= 0) { toast.error(t("outOfStock")); return; }
            addItem(product, 1);
            toast.success(t("addToCart"));
          })}
          className="mt-2 w-full bg-[#0052CC] hover:bg-[#003D99] text-white py-2 rounded-md text-sm font-semibold flex items-center justify-center gap-1.5 transition"
          data-testid={`add-to-cart-${product.product_id}`}
        >
          <ShoppingCart className="w-4 h-4" /> {t("addToCart")}
        </button>
      </div>
    </Link>
  );
}
